import { TextField, Typography } from '@client/new/components/basic';
import {
  Checkbox,
  Divider,
  Button,
  Switch,
  Select,
  MenuItem,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import {
  isVisibleOptions,
  modeOptions,
  tierOptions,
  paymentMethodOptions,
  landingPageVisualVersionOptions,
  landingPageThemeOptions,
} from './administrationConfig';
import { colors } from '@client/new/components/colors';
import _ from 'lodash';
import { RHFSelect } from '@client/new/components/rhfSelect';
import {
  AdministrationViewModel,
  LandingPageVisualVersion,
} from './useAdministration.viewModel';
import { InitialForm } from './useAdministration.model';
import ReactJson from 'react-json-view';
import { CopyPreviewLink } from './CopyPreviewLink';
import * as styles from './AdministrationForm.css';
import { Tags } from '@client/new/components/Tags/Tags';

export function AdministrationForm({
  control,
  register,
  dispatch,
  isPaymentEnabled,
  paymentMethod,
  tags,
  customizations,
  landingPageVisualVersion,
  landingPagePreviewUrl,
  stripeUserId,
  isDeepLinkDisabled,
  submittingReset,
  disableSuperAdminSections,
  showStripe,
  hidePublishedAt,
  hasAccessToOwnExclusiveSubscription,
  submittingGrantOwnSubscription,
}: AdministrationViewModel) {
  return (
    <div className="bg-white p-6 rounded-[20px]">
      <div className="flex flex-col">
        <p className="text-xl font-medium leading-6 mb-4">General</p>
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex flex-col w-[210px]">
            <span className="p-1 leading-5 text-sm font-medium">Stage</span>
            <RHFSelect<InitialForm>
              name="mode"
              control={control}
              options={modeOptions}
              disabled={disableSuperAdminSections}
            />
          </div>
          <div className="flex flex-col w-[210px]">
            <span className="p-1 leading-5 text-sm font-medium">Tier</span>
            <RHFSelect<InitialForm>
              name="tier"
              control={control}
              options={tierOptions}
            />
          </div>
          <div className="flex flex-col w-[210px]">
            <span className="p-1 leading-5 text-sm font-medium">
              Searchable
            </span>
            <RHFSelect<InitialForm>
              name="isVisible"
              control={control}
              options={isVisibleOptions}
              disabled={disableSuperAdminSections}
            />
          </div>
          {hidePublishedAt ? null : (
            <div className="flex flex-col w-[210px]">
              <span className="p-1 leading-5 text-sm font-medium">
                Published at
              </span>
              <TextField
                type="datetime-local"
                className="w-full"
                {...register('publishedAt')}
              />
            </div>
          )}
        </div>
        <div>
          <span className="p-1 leading-5 text-sm font-medium">
            Terms and Conditions Accepted
          </span>
          <Controller
            name={'isTCAccepted'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Checkbox checked={value} onChange={onChange} />
            )}
          />
        </div>
      </div>
      {showStripe ? (
        <>
          <Divider sx={{ marginY: '24px', borderColor: colors.neutral40 }} />
          <div className="flex flex-col">
            <p className="text-xl font-medium leading-6 mb-4">Payments</p>
            <div className="flex items-center">
              <span className="p-1 leading-5 text-sm font-medium">
                Payment status
              </span>
              <Controller
                name={'isPaymentEnabled'}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Switch checked={value} onChange={onChange} />
                )}
              />
              <span className="p-1 leading-5 text-sm font-medium">
                {isPaymentEnabled ? (
                  <Typography sx={{ color: colors.primary80 }}>
                    Enabled
                  </Typography>
                ) : (
                  <Typography sx={{ color: '#646464' }}>Disabled</Typography>
                )}
              </span>
            </div>
            <div className="flex flex-wrap gap-4 items-end mb-4">
              <div className="flex flex-col w-[210px]">
                <span className="p-1 leading-5 text-sm font-medium">
                  Payment method
                </span>
                <RHFSelect<InitialForm>
                  name="paymentMethod"
                  control={control}
                  options={paymentMethodOptions}
                />
              </div>
              <div>
                {stripeUserId && paymentMethod === 1 && (
                  <Button
                    variant="contained"
                    onClick={() => dispatch('resetStripe')}
                    disabled={submittingReset}
                  >
                    Reset Stripe
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      ) : null}
      {disableSuperAdminSections ? null : (
        <>
          <Divider sx={{ marginY: '24px', borderColor: colors.neutral40 }} />
          <div className="flex flex-col">
            <p className="text-xl font-medium leading-6 mb-4">Links & URLs</p>
            <div className="flex flex-col gap-4">
              <div className="max-w-[590px]">
                <span className="p-1 leading-5 text-sm font-medium">
                  Custom URL
                </span>
                <TextField
                  placeholder="Enter"
                  className="w-full"
                  {...register('customURL')}
                />
              </div>
              <div className="max-w-[590px]">
                <span className="p-1 leading-5 text-sm font-medium">
                  Deep Link
                </span>
                <TextField
                  placeholder="Enter"
                  disabled={isDeepLinkDisabled}
                  className="w-full"
                  {...register('profileDeepLink')}
                />
              </div>
            </div>
          </div>
          <Divider sx={{ marginY: '24px', borderColor: colors.neutral40 }} />
          <div>
            <p className="text-xl font-medium leading-6 mb-4">Tags</p>
            <Tags
              values={tags}
              onChange={(values) => dispatch('setTags', values)}
            />
          </div>
        </>
      )}
      <Divider sx={{ marginY: '24px', borderColor: colors.neutral40 }} />
      <div className="flex flex-col">
        <p className="text-xl font-medium leading-6 mb-4">
          Own subscription access
        </p>
        <div className="flex items-center gap-4">
          <Typography sx={{ fontSize: '14px' }}>
            {hasAccessToOwnExclusiveSubscription
              ? 'Access granted'
              : 'No access'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => dispatch('grantOwnSubscriptionAccess')}
            disabled={
              hasAccessToOwnExclusiveSubscription ||
              submittingGrantOwnSubscription
            }
          >
            Grant access
          </Button>
        </div>
      </div>
      {disableSuperAdminSections ? null : (
        <>
          <Divider sx={{ marginY: '24px', borderColor: colors.neutral40 }} />
          <div className="flex flex-col">
            <p className={styles.sectionTitle}>Landing page</p>
            <div className={styles.selectRow}>
              <div className={styles.selectField}>
                <span
                  id="label-landingPageVisualVersion"
                  className={styles.selectLabel}
                >
                  Visual version
                </span>
                <Select<LandingPageVisualVersion>
                  id="select-landingPageVisualVersion"
                  labelId="label-landingPageVisualVersion"
                  value={landingPageVisualVersion}
                  onChange={(event) =>
                    dispatch(
                      'setLandingPageVisualVersion',
                      event.target.value as LandingPageVisualVersion,
                    )
                  }
                  className={styles.select}
                >
                  {landingPageVisualVersionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className={styles.selectField}>
                <span
                  id="label-landingPageTheme"
                  className={styles.selectLabel}
                >
                  Theme
                </span>
                <Controller
                  name="landingPageTheme"
                  control={control}
                  render={({ field }) => (
                    <Select<string>
                      id="select-landingPageTheme"
                      labelId="label-landingPageTheme"
                      value={field.value ?? ''}
                      onChange={(event) =>
                        field.onChange(event.target.value || null)
                      }
                      displayEmpty
                      className={styles.select}
                    >
                      {landingPageThemeOptions.map((option) => (
                        <MenuItem key={option.label} value={option.value ?? ''}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </div>
            </div>
            <CopyPreviewLink url={landingPagePreviewUrl} />
            <Typography sx={{ fontSize: '14px', color: '#646464' }}>
              The theme only applies when the visual version is 2. Changes reach
              the live landing pages within about a minute.
            </Typography>
          </div>
          <Divider sx={{ marginY: '24px', borderColor: colors.neutral40 }} />
          <div className="w-full">
            <p className="text-xl font-medium leading-6 mb-4">Customizations</p>
            <ReactJson
              src={customizations}
              collapsed={false}
              name={'customizations'}
              onEdit={(edit) => dispatch('setCustomizations', edit.updated_src)}
              onAdd={(add) => dispatch('setCustomizations', add.updated_src)}
              onDelete={(del) => dispatch('setCustomizations', del.updated_src)}
            />
          </div>
        </>
      )}
    </div>
  );
}
